import React from 'react'

/**
 * CSS Modifier suffixes for the CTA block's button. Keys mirror the values the admin
 * panel's block editor saves (`buttonSize` / `buttonShape` / `buttonAlign`), so any
 * value coming back from the API maps directly onto a class here — see
 * blog-components.scss for the matching `.cta-btn--*` / `.blog-cta--*` rules. Kept
 * deliberately separate from Media's `BUTTON_*_CLASSES` even though the size/shape
 * options are identical, since the two blocks style different elements
 * (`.cta-btn` is a normal in-flow button; `.media-cta-btn` is absolutely positioned).
 */
const BUTTON_SIZE_CLASSES = {
    sm: 'cta-btn--sm',
    md: 'cta-btn--md',
    lg: 'cta-btn--lg',
}

const BUTTON_SHAPE_CLASSES = {
    square: 'cta-btn--square',
    rounded: 'cta-btn--rounded',
}

const ALIGN_CLASSES = {
    left: 'blog-cta--left',
    center: 'blog-cta--center',
    right: 'blog-cta--right',
}

const DEFAULT_BUTTON_TEXT = 'Learn More'

/**
 * Standalone call-to-action block: a themed button (the one required part) linking
 * out to `data.link`, plus an optional heading/description above it — each shown
 * only when its own admin toggle (`showHeading` / `showDescription`) is on, so an
 * unset one doesn't leave blank space in the post. The button reuses the same
 * background/radius/hover language as the rest of the blog's `.btn-primary` buttons
 * (see blog.scss) rather than a one-off style, just with the size/shape the admin picked.
 * @param {Object} props
 * @param {Object} props.data - Block data (ctaHeading, showHeading, ctaDescription, showDescription, link, buttonText, buttonSize, buttonShape, buttonAlign)
 * @returns {JSX.Element|null}
 */
const Cta = ({ data }) => {
    if (!data) {
        return null
    }

    const { link } = data
    // A CTA with nowhere to send the reader isn't worth rendering — matches the
    // admin's isBlockEmpty check, but the public site guards against stale/legacy data too.
    if (!link) {
        return null
    }

    const showHeading = !!data.showHeading && !!data.ctaHeading
    const showDescription = !!data.showDescription && !!data.ctaDescription
    const buttonText = data.buttonText?.trim() || DEFAULT_BUTTON_TEXT
    const buttonClassName = [
        'cta-btn',
        BUTTON_SIZE_CLASSES[data.buttonSize] || BUTTON_SIZE_CLASSES.md,
        BUTTON_SHAPE_CLASSES[data.buttonShape] || BUTTON_SHAPE_CLASSES.square,
    ].join(' ')

    return (
        <div className={['blog-cta', ALIGN_CLASSES[data.buttonAlign] || ALIGN_CLASSES.center].join(' ')}>
            {showHeading ? <h3 className="cta-heading">{data.ctaHeading}</h3> : null}
            {showDescription ? <p className="cta-description">{data.ctaDescription}</p> : null}
            <a href={link} target="_blank" rel="noopener noreferrer" className={buttonClassName}>
                {buttonText}
            </a>
        </div>
    )
}

export default Cta
